<template>
  <main class="customer-page">
    <section class="soft-card table-panel">
      <div class="toolbar">
        <el-input
          v-model="searchQuery"
          placeholder="搜索客户名称或编码..."
          size="small"
          clearable
          class="search-input"
          @keyup.enter.native="onSearch" @clear="onSearch"
        />
        <el-button size="small" plain icon="el-icon-search" @click="onSearch">搜索</el-button>
        <el-button type="primary" size="small" icon="el-icon-plus" @click="onAdd">新增</el-button>
        <el-button size="small" type="info" icon="el-icon-search" :loading="reconciling" @click="onReconcile">对账</el-button>
      </div>
      <div class="desktop-only">
        <el-table :data="customers" size="small" style="width: 100%" @sort-change="onSortChange">
          <el-table-column type="index" label="序号" width="60" :index="pageIndex" />
          <el-table-column label="ID" width="110">
            <template slot-scope="scope">
              <div class="id-cell">
                <span class="id-text" :title="scope.row.id">{{ scope.row.id }}</span>
                <el-button type="text" size="mini" icon="el-icon-document-copy" class="copy-btn" @click="copyId(scope.row.id)" />
              </div>
            </template>
          </el-table-column>
          <el-table-column prop="code" label="编码" width="80" show-overflow-tooltip />
          <el-table-column prop="name" label="客户名称" min-width="140" show-overflow-tooltip />
          <el-table-column prop="email" label="邮箱" min-width="180" show-overflow-tooltip />
          <el-table-column label="总 token" width="160" sortable="custom" prop="totalTokens">
            <template slot-scope="scope">{{ $formatNumber(scope.row.totalTokens) }}</template>
          </el-table-column>
          <el-table-column label="总费用" width="160" sortable="custom" prop="totalCost">
            <template slot-scope="scope">¥ {{ $formatNumber(scope.row.totalCost) }}</template>
          </el-table-column>
          <el-table-column label="余额" width="150">
            <template slot-scope="scope">¥ {{ $formatNumber(scope.row.balance) }}</template>
          </el-table-column>
          <el-table-column label="成本" width="140">
            <template slot-scope="scope">¥ {{ $formatNumber(scope.row.supplierCost) }}</template>
          </el-table-column>
          <el-table-column label="利润" width="140">
            <template slot-scope="scope">¥ {{ $formatNumber(scope.row.profit) }}</template>
          </el-table-column>
          <el-table-column prop="apiKeyPrefix" label="Key 前缀" width="130" show-overflow-tooltip />
          <el-table-column prop="status" label="状态" width="100">
            <template slot-scope="scope">
              <el-tag :type="scope.row.status === '已激活' ? 'success' : 'info'" size="small">
                {{ scope.row.status || '未激活' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="关联供应商" min-width="200">
            <template slot-scope="scope">
              <el-tag v-for="s in (scope.row.suppliers || [])" :key="s.id" size="small" style="margin-right:4px;margin-bottom:2px;">
                {{ s.name }}
              </el-tag>
              <span v-if="!scope.row.suppliers || scope.row.suppliers.length === 0" style="color:var(--muted-2);">-</span>
            </template>
          </el-table-column>
          <el-table-column label="创建时间" width="180">
            <template slot-scope="scope">{{ $formatTime(scope.row.createdAt) }}</template>
          </el-table-column>
          <el-table-column label="操作" width="420" fixed="right">
            <template slot-scope="scope">
              <el-button size="small" type="text" @click="onBilling(scope.row)">账单</el-button>
              <el-button size="small" type="text" @click="onModelPrice(scope.row)">模型价格</el-button>
              <el-button size="small" type="text" style="color:#67c23a;" @click="onRecharge(scope.row)">充值</el-button>
              <el-button size="small" type="text" @click="onBalanceLog(scope.row)">充值记录</el-button>
              <el-button v-if="scope.row.status !== '已激活'" size="small" type="text" class="send-code-btn" @click="onSendCode(scope.row)">发送验证码</el-button>
              <el-button size="small" type="text" @click="onEdit(scope.row)">编辑</el-button>
              <el-button v-if="scope.row.status !== '未激活'" size="small" type="text" :class="{ 'delete-btn': true, 'enable-btn': scope.row.status === '已禁用' }" @click="onToggleStatus(scope.row)">
                {{ scope.row.status === '已禁用' ? '启用' : '禁用' }}
              </el-button>
              <el-button size="small" type="text" class="delete-btn" @click="onDelete(scope.row)">删除</el-button>
            </template>
          </el-table-column>
        </el-table>
        <el-pagination
          class="pagination-bar"
          layout="total, prev, pager, next"
          small
          :total="total"
          :page-size="pageSize"
          :current-page.sync="currentPage"
          @current-change="onPageChange"
        />
      </div>
      <div class="mobile-only list-cards">
        <div v-for="row in customers" :key="row.id" class="list-card">
          <div class="card-header">
            <div>
              <div class="card-title">{{ row.name }}</div>
              <div class="card-sub">{{ row.code }} · {{ row.email || '-' }}</div>
            </div>
            <el-tag :type="row.status === '已激活' ? 'success' : 'info'" size="small">{{ row.status || '未激活' }}</el-tag>
          </div>
          <div class="stats-row">
            <div class="stat-item">
              <div class="stat-value">¥ {{ $formatNumber(row.totalCost) }}</div>
              <div class="stat-label">总费用</div>
            </div>
            <div class="stat-item">
              <div class="stat-value" style="color:#10b981;">¥ {{ $formatNumber(row.balance) }}</div>
              <div class="stat-label">余额</div>
            </div>
            <div class="stat-item">
              <div class="stat-value" style="color:#f4b74f;">¥ {{ $formatNumber(row.profit) }}</div>
              <div class="stat-label">利润</div>
            </div>
          </div>
          <div style="display:flex;flex-wrap:wrap;gap:4px;margin-bottom:6px;">
            <el-tag v-for="s in (row.suppliers || [])" :key="s.id" size="mini" style="font-size:10px;">{{ s.name }}</el-tag>
            <span v-if="!row.suppliers || row.suppliers.length === 0" style="font-size:11px;color:rgba(255,255,255,.35);">-</span>
          </div>
          <div class="meta-row">
            <span>总 token: {{ $formatNumber(row.totalTokens) }}</span>
            <span>成本: ¥ {{ $formatNumber(row.supplierCost) }}</span>
            <span v-if="row.apiKeyPrefix">Key: {{ row.apiKeyPrefix }}***</span>
            <span>{{ $formatTime(row.createdAt) }}</span>
          </div>
          <div class="card-actions">
            <el-button size="small" type="text" @click="onBilling(row)">账单</el-button>
            <el-button size="small" type="text" @click="onModelPrice(row)">模型价格</el-button>
            <el-button size="small" type="text" style="color:#67c23a;" @click="onRecharge(row)">充值</el-button>
            <el-button size="small" type="text" @click="onBalanceLog(row)">充值记录</el-button>
            <el-button v-if="row.status !== '已激活'" size="small" type="text" style="color:#f59e0b;" @click="onSendCode(row)">发送验证码</el-button>
            <el-button size="small" type="text" @click="onEdit(row)">编辑</el-button>
            <el-button v-if="row.status !== '未激活'" size="small" type="text" :class="{ 'delete-btn': true, 'enable-btn': row.status === '已禁用' }" @click="onToggleStatus(row)">
              {{ row.status === '已禁用' ? '启用' : '禁用' }}
            </el-button>
            <el-button size="small" type="text" class="delete-btn" @click="onDelete(row)">删除</el-button>
          </div>
        </div>
        <el-pagination
          class="pagination-bar"
          layout="total, prev, pager, next"
          small
          :total="total"
          :page-size="pageSize"
          :current-page.sync="currentPage"
          @current-change="onPageChange"
        />
      </div>
    </section>
    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="560px" :close-on-click-modal="false">
      <el-form ref="form" :model="form" :rules="rules" label-width="100px" label-position="right" size="small">
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="编码" prop="code">
              <el-input v-model="form.code" placeholder="例: CUST001（用于登录）" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="Key 前缀" prop="apiKeyPrefix">
              <el-input v-model="form.apiKeyPrefix" placeholder="例: kx_" clearable />
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="访问域名" prop="baseUrl">
          <el-input v-model="form.baseUrl" placeholder="例: https://api.kexun.com（留空用默认域名）" clearable />
        </el-form-item>
        <el-form-item label="名称" prop="name">
          <el-input v-model="form.name" placeholder="例: 深圳科讯" />
        </el-form-item>
        <el-form-item label="邮箱" prop="email">
          <el-input v-model="form.email" placeholder="admin@kexun.com" clearable />
        </el-form-item>
        <el-form-item label="供应商" prop="supplierIds">
          <el-select v-model="form.supplierIds" multiple placeholder="请选择模型供应商（可多选）" style="width: 100%">
            <el-option
              v-for="s in suppliers"
              :key="s.id"
              :label="s.name"
              :value="s.id"
            />
          </el-select>
        </el-form-item>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="缓存转输入">
              <el-input-number v-model="form.cacheToInputRatio" :min="0" :max="1" :step="0.1" :precision="2" :controls="false" style="width: 100%" placeholder="0~1，如 0.5" />
              <div class="form-tip">缓存命中 token 按此比例计入输入（0=不计，1=全计）</div>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="Token 倍率">
              <el-input-number v-model="form.tokenMultiplier" :min="1" :step="0.1" :precision="2" :controls="false" style="width: 100%" placeholder="如 1.2 表示多收 20%" />
              <div class="form-tip">向客户收取的 token 乘以该倍率（1=不加价）</div>
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
      <span slot="footer">
        <el-button @click="dialogVisible = false" size="small">取消</el-button>
        <el-button type="primary" @click="submitForm" size="small" :loading="submitting">确定</el-button>
      </span>
    </el-dialog>

    <!-- 充值对话框 -->
    <el-dialog title="客户充值" :visible.sync="rechargeDialogVisible" width="420px" :close-on-click-modal="false" top="25vh">
      <el-form ref="rechargeForm" :model="rechargeForm" :rules="rechargeRules" label-width="80px" size="small">
        <el-form-item label="客户">
          <span style="font-weight:600;">{{ rechargeCustomerName }}</span>
        </el-form-item>
        <el-form-item label="金额" prop="amount">
          <el-input v-model="rechargeForm.amount" placeholder="请输入充值金额" type="text">
            <template slot="prepend">¥</template>
          </el-input>
        </el-form-item>
        <el-form-item label="备注" prop="remark">
          <el-input v-model="rechargeForm.remark" placeholder="可选备注" type="text" />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="rechargeDialogVisible = false" size="small">取消</el-button>
        <el-button type="primary" @click="submitRecharge" size="small" :loading="rechargeSubmitting">确认充值</el-button>
      </span>
    </el-dialog>

    <!-- 充值记录对话框 -->
    <el-dialog title="充值记录" :visible.sync="balanceLogDialogVisible" width="850px" :close-on-click-modal="false" top="8vh">
      <div style="margin-bottom:12px;font-size:14px;color:var(--muted);">
        客户：<strong>{{ balanceLogCustomerName }}</strong>
        &nbsp;&nbsp;|&nbsp;&nbsp;
        当前余额：<strong style="color:#10b981;">¥ {{ $formatNumber(balanceLogCurrentBalance) }}</strong>
      </div>
      <el-table :data="balanceLogs" size="small" style="width:100%" :max-height="500">
        <el-table-column label="时间" width="150">
          <template slot-scope="scope">{{ $formatTime(scope.row.createdAt) }}</template>
        </el-table-column>
        <el-table-column label="类型" width="70">
          <template>
            <el-tag type="success" size="small">充值</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="金额" width="160">
          <template slot-scope="scope">
            <span style="color:#10b981;font-weight:600;white-space:nowrap;">
              +¥ {{ $formatNumber(scope.row.amount) }}
            </span>
          </template>
        </el-table-column>
        <el-table-column label="操作前余额" width="160">
          <template slot-scope="scope"><span style="white-space:nowrap;">¥ {{ $formatNumber(scope.row.balanceBefore) }}</span></template>
        </el-table-column>
        <el-table-column label="操作后余额" width="160">
          <template slot-scope="scope"><span style="white-space:nowrap;">¥ {{ $formatNumber(scope.row.balanceAfter) }}</span></template>
        </el-table-column>
        <el-table-column label="备注" min-width="100">
          <template slot-scope="scope">{{ scope.row.remark || '-' }}</template>
        </el-table-column>
      </el-table>
      <el-pagination
        class="pagination-bar"
        layout="total, prev, pager, next"
        small
        :total="balanceLogTotal"
        :page-size="balanceLogPageSize"
        :current-page.sync="balanceLogPage"
        @current-change="onBalanceLogPageChange"
      />
    </el-dialog>

    <!-- 账单对话框 -->
    <el-dialog :title="`客户账单 - ${billingCustomerName}`" :visible.sync="billingDialogVisible" fullscreen class="billing-fullscreen-dialog" :close-on-click-modal="false" @closed="onBillingClosed">
      <!-- Period tabs -->
      <el-tabs v-model="billingActivePeriod" @tab-click="onBillingPeriodChange">
        <el-tab-pane label="年度" name="annual" />
        <el-tab-pane label="月度" name="monthly" />
      </el-tabs>

      <!-- Summary cards -->
      <section class="billing-summary-cards">
        <div class="billing-summary-card">
          <div class="billing-card-label">总 Token</div>
          <div class="billing-card-value">{{ $formatNumber(billingSummary.total_tokens) }}</div>
        </div>
        <div class="billing-summary-card">
          <div class="billing-card-label">费用</div>
          <div class="billing-card-value" style="color:#34c98f;">¥ {{ $formatNumber(billingSummary.total_cost) }}</div>
        </div>
        <div class="billing-summary-card">
          <div class="billing-card-label">供应商成本</div>
          <div class="billing-card-value" style="color:var(--muted-2);">¥ {{ $formatNumber(billingSummary.supplier_cost) }}</div>
        </div>
        <div class="billing-summary-card">
          <div class="billing-card-label">利润</div>
          <div class="billing-card-value" :class="billingSummary.profit >= 0 ? 'profit-positive' : 'profit-negative'">
            ¥ {{ $formatNumber(billingSummary.profit) }}
          </div>
        </div>
        <div class="billing-summary-card">
          <div class="billing-card-label">调用次数</div>
          <div class="billing-card-value">{{ $formatNumber(billingSummary.total_calls) }}</div>
        </div>
      </section>

      <!-- Trend chart -->
      <section class="billing-chart-panel" v-if="billingTrendData.length">
        <div class="billing-panel-head">
          <h3 class="billing-panel-title">消费趋势</h3>
          <span class="billing-panel-sub">{{ billingRangeLabel }}</span>
        </div>
        <div ref="billingTrendChart" class="billing-chart-container" />
      </section>

      <!-- Items table -->
      <section class="billing-table-panel">
        <div class="billing-toolbar">
          <span class="billing-toolbar-title">{{ billingPeriodTitle }}</span>
          <el-date-picker
            v-if="billingActivePeriod === 'annual'"
            v-model="billingPickerYear"
            type="year"
            placeholder="选择年份"
            size="small"
            value-format="yyyy"
            class="billing-date-picker"
            @change="onBillingDateChange"
          />
          <el-date-picker
            v-else
            v-model="billingPickerMonth"
            type="month"
            placeholder="选择月份"
            size="small"
            value-format="yyyy-MM"
            class="billing-date-picker"
            @change="onBillingDateChange"
          />
          <el-button size="small" icon="el-icon-download" @click="onBillingExport">导出</el-button>
        </div>
        <el-table :data="billingItems" size="small" style="width: 100%" v-loading="billingLoading">
          <el-table-column type="index" label="序号" width="60" :index="billingPageIndex" />
          <el-table-column label="周期">
            <template slot-scope="scope">
              <el-button v-if="scope.row.year" type="text" size="small" @click="onBillingDrillDown(scope.row)">
                {{ scope.row.label }}
              </el-button>
              <span v-else>{{ scope.row.label }}</span>
            </template>
          </el-table-column>
          <el-table-column label="总 Token" align="right" min-width="110">
            <template slot-scope="scope">{{ $formatNumber(scope.row.total_tokens) }}</template>
          </el-table-column>
          <el-table-column label="调用次数" align="right" min-width="90">
            <template slot-scope="scope">{{ scope.row.total_calls }}</template>
          </el-table-column>
          <el-table-column label="费用" align="right" min-width="110">
            <template slot-scope="scope">¥ {{ $formatNumber(scope.row.total_cost) }}</template>
          </el-table-column>
          <el-table-column label="供应商成本" align="right" min-width="110">
            <template slot-scope="scope">¥ {{ $formatNumber(scope.row.supplier_cost) }}</template>
          </el-table-column>
          <el-table-column label="利润" align="right" min-width="110">
            <template slot-scope="scope">
              <span :class="scope.row.profit >= 0 ? 'profit-positive' : 'profit-negative'">
                ¥ {{ $formatNumber(scope.row.profit) }}
              </span>
            </template>
          </el-table-column>
        </el-table>
        <el-pagination
          class="pagination-bar"
          layout="total, prev, pager, next"
          small
          :total="billingTotal"
          :page-size="billingPageSize"
          :current-page.sync="billingPage"
          @current-change="onBillingPageChange"
        />
      </section>
    </el-dialog>
  </main>
</template>

<script>
export default {
  name: 'CustomerPage',
  data() {
    return {
      customers: [],
      suppliers: [],
      total: 0,
      pageSize: 10,
      currentPage: 1,
      searchQuery: '',
      sortField: '',
      sortOrder: '',
      dialogVisible: false,
      dialogTitle: '',
      submitting: false,
      form: {
        id: null,
        code: '',
        name: '',
        email: '',
        apiKeyPrefix: '',
        baseUrl: '',
        supplierIds: [],
        cacheToInputRatio: 0,
        tokenMultiplier: 1
      },
      rules: {
        code: [
          { required: true, message: '请输入客户编码', trigger: 'blur' }
        ],
        name: [
          { required: true, message: '请输入客户名称', trigger: 'blur' }
        ],
        supplierIds: [
          { required: true, message: '请至少选择一个供应商', trigger: 'change' }
        ]
      },
      // 充值
      rechargeDialogVisible: false,
      rechargeSubmitting: false,
      rechargeCustomerId: null,
      rechargeCustomerName: '',
      rechargeForm: {
        amount: '',
        remark: ''
      },
      rechargeRules: {
        amount: [
          { required: true, message: '请输入充值金额', trigger: 'blur' },
          { validator: (rule, value, callback) => {
            const num = parseFloat(value)
            if (isNaN(num) || num <= 0) {
              callback(new Error('金额必须大于 0'))
            } else {
              callback()
            }
          }, trigger: 'blur' }
        ]
      },
      // 余额记录
      balanceLogDialogVisible: false,
      balanceLogCustomerId: null,
      balanceLogCustomerName: '',
      balanceLogCurrentBalance: 0,
      balanceLogs: [],
      balanceLogTotal: 0,
      balanceLogPageSize: 10,
      balanceLogPage: 1,
      // 账单
      billingDialogVisible: false,
      billingCustomerId: null,
      billingCustomerName: '',
      billingActivePeriod: 'annual',
      billingPickerYear: String(new Date().getFullYear()),
      billingPickerMonth: new Date().getFullYear() + '-' + String(new Date().getMonth() + 1).padStart(2, '0'),
      billingSummary: {},
      billingTrendData: [],
      billingItems: [],
      billingTotal: 0,
      billingPageSize: 10,
      billingPage: 1,
      billingLoading: false,
      billingChart: null,
      reconciling: false,
      fixing: false
    }
  },
  computed: {
    pageIndex() {
      return (this.currentPage - 1) * this.pageSize + 1
    },
    billingPageIndex() {
      return (this.billingPage - 1) * this.billingPageSize + 1
    },
    billingPeriodTitle() {
      const map = { monthly: '月度账单', annual: '年度账单' }
      return map[this.billingActivePeriod] || '账单'
    },
    billingRangeLabel() {
      return this.billingActivePeriod === 'annual' ? this.billingPickerYear + '年' : this.billingPickerMonth
    }
  },
  mounted() {
    this.fetchCustomers()
    this.fetchSuppliers()
  },
  methods: {
    copyId(id) {
      const text = String(id)
      // 降级方案：使用 textarea + execCommand
      const textarea = document.createElement('textarea')
      textarea.value = text
      textarea.style.position = 'fixed'
      textarea.style.opacity = '0'
      document.body.appendChild(textarea)
      textarea.select()
      try {
        document.execCommand('copy')
        this.$message.success('ID 已复制')
      } catch {
        this.$message.error('复制失败')
      }
      document.body.removeChild(textarea)
    },
    onPageChange(page) {
      this.currentPage = page
      this.fetchCustomers()
    },
    onSearch() {
      this.currentPage = 1
      this.fetchCustomers()
    },
    onSortChange({ prop, order }) {
      if (prop && order) {
        const fieldMap = { totalTokens: 'total_tokens', totalCost: 'total_cost' }
        this.sortField = fieldMap[prop] || prop
        this.sortOrder = order === 'ascending' ? 'asc' : 'desc'
      } else {
        this.sortField = ''
        this.sortOrder = ''
      }
      this.currentPage = 1
      this.fetchCustomers()
    },
    async fetchCustomers() {
      try {
        const res = await this.$http.get('/admin/customers', {
          params: {
            keyword: this.searchQuery || undefined,
            page: this.currentPage,
            pageSize: this.pageSize,
            sortField: this.sortField || undefined,
            sortOrder: this.sortOrder || undefined
          }
        })
        this.customers = res.records || []
        this.total = Number(res.total) || 0
      } catch {
        this.$message.error('加载客户列表失败')
      }
    },
    async fetchSuppliers() {
      try {
        const res = await this.$http.get('/admin/suppliers', {
          params: { page: 1, pageSize: 1000 }
        })
        this.suppliers = res.records || []
      } catch {
        this.$message.error('加载供应商列表失败')
      }
    },
    onAdd() {
      this.dialogTitle = '新增客户'
      this.form = { id: null, code: '', name: '', email: '', apiKeyPrefix: '', baseUrl: '', supplierIds: [], cacheToInputRatio: 0, tokenMultiplier: 1 }
      this.dialogVisible = true
      this.$nextTick(() => {
        this.$refs.form?.clearValidate()
      })
    },
    onEdit(row) {
      this.dialogTitle = '编辑客户'
      this.form = {
        id: row.id,
        code: row.code,
        name: row.name,
        email: row.email || '',
        apiKeyPrefix: row.apiKeyPrefix || '',
        baseUrl: row.baseUrl || '',
        supplierIds: (row.suppliers || []).map(s => s.id),
        cacheToInputRatio: row.cacheToInputRatio != null ? Number(row.cacheToInputRatio) : 0,
        tokenMultiplier: row.tokenMultiplier != null ? Number(row.tokenMultiplier) : 1
      }
      this.dialogVisible = true
      this.$nextTick(() => this.$refs.form?.clearValidate())
    },
    submitForm() {
      this.$refs.form.validate(async valid => {
        if (!valid) return
        this.submitting = true
        try {
          const payload = {
            code: this.form.code,
            name: this.form.name,
            email: this.form.email,
            apiKeyPrefix: this.form.apiKeyPrefix || null,
            baseUrl: this.form.baseUrl || null,
            supplierIds: this.form.supplierIds,
            cacheToInputRatio: this.form.cacheToInputRatio != null ? this.form.cacheToInputRatio : 0,
            tokenMultiplier: this.form.tokenMultiplier != null ? this.form.tokenMultiplier : 1
          }
          if (this.form.id) {
            await this.$http.put('/admin/customers/' + this.form.id, payload)
            this.$message.success('编辑成功')
          } else {
            await this.$http.post('/admin/customers', payload)
            this.$message.success('新增成功，系统已发送激活验证码至 ' + this.form.email)
          }
          this.dialogVisible = false
          this.fetchCustomers()
        } catch {
          this.$message.error('操作失败')
        } finally {
          this.submitting = false
        }
      })
    },
    onModelPrice(row) {
      this.$router.push('/customer-models/' + row.id + '?name=' + encodeURIComponent(row.name))
    },
    onDelete(row) {
      this.$confirm('确定删除客户「' + row.name + '」？', '确认删除', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        return this.$http.delete('/admin/customers/' + row.id)
      }).then(() => {
        this.$message.success('删除成功')
        this.fetchCustomers()
      }).catch(e => {
        if (e === 'cancel' || e === 'close') return
        this.$message.error(e.response?.data?.error || '删除失败')
      })
    },
    async onSendCode(row) {
      try {
        await this.$http.post('/admin/customers/' + row.id + '/send-code')
        this.$message.success('验证码已发送至 ' + row.email)
      } catch {
        this.$message.error('发送失败')
      }
    },
    async onToggleStatus(row) {
      const action = row.status === '已禁用' ? '启用' : '禁用'
      try {
        await this.$http.put('/admin/customers/' + row.id + '/toggle-status')
        this.$message.success(action + '成功')
        this.fetchCustomers()
      } catch {
        this.$message.error('操作失败')
      }
    },
    // ====== 余额操作 ======
    onRecharge(row) {
      this.rechargeCustomerId = row.id
      this.rechargeCustomerName = row.name
      this.rechargeForm = { amount: '', remark: '' }
      this.rechargeDialogVisible = true
      this.$nextTick(() => this.$refs.rechargeForm?.clearValidate())
    },
    submitRecharge() {
      this.$refs.rechargeForm.validate(async valid => {
        if (!valid) return
        this.rechargeSubmitting = true
        try {
          await this.$http.post('/admin/customers/' + this.rechargeCustomerId + '/recharge', {
            amount: parseFloat(this.rechargeForm.amount),
            remark: this.rechargeForm.remark || ''
          })
          this.$message.success('充值申请已提交')
          this.rechargeDialogVisible = false
          this.fetchCustomers()
        } catch (e) {
          this.$message.error(e.response?.data?.error || '充值失败')
        } finally {
          this.rechargeSubmitting = false
        }
      })
    },
    onBalanceLog(row) {
      this.balanceLogCustomerId = row.id
      this.balanceLogCustomerName = row.name
      this.balanceLogCurrentBalance = row.balance || 0
      this.balanceLogPage = 1
      this.fetchBalanceLogs()
      this.balanceLogDialogVisible = true
    },
    onBalanceLogPageChange(page) {
      this.balanceLogPage = page
      this.fetchBalanceLogs()
    },
    async fetchBalanceLogs() {
      try {
        const res = await this.$http.get('/admin/customers/' + this.balanceLogCustomerId + '/balance-logs', {
          params: {
            page: this.balanceLogPage,
            pageSize: this.balanceLogPageSize
          }
        })
        this.balanceLogs = res.records || []
        this.balanceLogTotal = Number(res.total) || 0
      } catch {
        this.$message.error('加载余额记录失败')
      }
    },
    // ====== 账单操作 ======
    onBilling(row) {
      this.billingCustomerId = row.id
      this.billingCustomerName = row.name
      this.billingActivePeriod = 'annual'
      this.billingPickerYear = String(new Date().getFullYear())
      this.billingPickerMonth = new Date().getFullYear() + '-' + String(new Date().getMonth() + 1).padStart(2, '0')
      this.billingPage = 1
      this.billingDialogVisible = true
      this.$nextTick(() => this.fetchBillingAll())
    },
    onBillingClosed() {
      if (this.billingChart) {
        this.billingChart.dispose()
        this.billingChart = null
      }
    },
    async fetchBillingAll() {
      this.billingLoading = true
      try {
        const params = this.buildBillingParams()
        const [summary, trend] = await Promise.all([
          this.$http.get('/admin/billing/summary', { params }),
          this.$http.get('/admin/billing/trend', { params: { period: params.period, year: params.year, month: params.month, customerId: this.billingCustomerId } })
        ])
        this.billingSummary = summary
        this.billingTrendData = trend || []
        await this.fetchBillingItems()
        this.$nextTick(() => this.renderBillingChart())
      } catch {
        this.$message.error('加载账单数据失败')
      } finally {
        this.billingLoading = false
      }
    },
    async fetchBillingItems() {
      try {
        const params = { ...this.buildBillingParams(), customerId: this.billingCustomerId, page: this.billingPage, pageSize: this.billingPageSize }
        const res = await this.$http.get('/admin/billing/items', { params })
        this.billingItems = res.records || []
        this.billingTotal = Number(res.total) || 0
      } catch {
        this.$message.error('加载账单明细失败')
      }
    },
    buildBillingParams() {
      const params = { period: this.billingActivePeriod, customerId: this.billingCustomerId }
      if (this.billingActivePeriod === 'annual') {
        params.year = parseInt(this.billingPickerYear) || new Date().getFullYear()
      } else {
        const parts = (this.billingPickerMonth || '').split('-')
        params.year = parseInt(parts[0]) || new Date().getFullYear()
        params.month = parseInt(parts[1]) || (new Date().getMonth() + 1)
      }
      return params
    },
    onBillingPeriodChange() {
      this.billingPage = 1
      this.fetchBillingAll()
    },
    onBillingDateChange() {
      this.billingPage = 1
      this.fetchBillingAll()
    },
    onBillingPageChange(page) {
      this.billingPage = page
      this.fetchBillingItems()
    },
    onBillingDrillDown(row) {
      if (this.billingActivePeriod === 'annual' && row.year && row.month) {
        this.billingActivePeriod = 'monthly'
        this.billingPickerMonth = row.year + '-' + String(row.month).padStart(2, '0')
        this.fetchBillingAll()
      }
    },
    onBillingExport() {
      const params = this.buildBillingParams()
      this.$http.get('/admin/billing/export', { params }).then(records => {
        if (!records || !records.length) {
          this.$message.info('没有数据可导出')
          return
        }
        const data = records.map(r => ({
          '周期': r.label,
          '总Token': r.total_tokens,
          '调用次数': r.total_calls,
          '费用': r.total_cost,
          '供应商成本': r.supplier_cost,
          '利润': r.profit
        }))
        const XLSX = require('xlsx')
        const ws = XLSX.utils.json_to_sheet(data)
        const wb = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(wb, ws, this.billingPeriodTitle)
        const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' })
        const blob = new Blob([wbout], { type: 'application/octet-stream' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        const ts = new Date()
        const pad = n => String(n).padStart(2, '0')
        a.download = `${this.billingCustomerName}_${this.billingPeriodTitle}_${ts.getFullYear()}${pad(ts.getMonth()+1)}${pad(ts.getDate())}${pad(ts.getHours())}${pad(ts.getMinutes())}${pad(ts.getSeconds())}.xlsx`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
        this.$message.success('导出成功')
      }).catch(() => {
        this.$message.error('导出失败')
      })
    },
    renderBillingChart() {
      const el = this.$refs.billingTrendChart
      if (!el || !this.billingTrendData.length) return
      if (this.billingChart) this.billingChart.dispose()
      const echarts = require('echarts')
      const chart = echarts.init(el)
      this.billingChart = chart

      const dates = this.billingTrendData.map(d => this.formatBillingLabel(d.date))
      const costs = this.billingTrendData.map(d => Number(d.cost) || 0)

      chart.setOption({
        tooltip: {
          trigger: 'axis',
          backgroundColor: 'rgba(20,25,40,0.9)',
          borderColor: 'rgba(133,164,255,0.2)',
          textStyle: { color: '#c8d0e0', fontSize: 12 }
        },
        grid: { left: 60, right: 20, top: 20, bottom: 30 },
        xAxis: {
          type: 'category',
          data: dates,
          axisLine: { lineStyle: { color: 'rgba(133,164,255,0.15)' } },
          axisLabel: { color: '#9aaac1', fontSize: 11, rotate: dates.length > 12 ? 45 : 0 }
        },
        yAxis: {
          type: 'value',
          splitLine: { lineStyle: { color: 'rgba(133,164,255,0.08)' } },
          axisLabel: { color: '#9aaac1', fontSize: 11, formatter: v => '¥' + v }
        },
        series: [{
          type: 'line',
          data: costs,
          smooth: true,
          symbol: 'none',
          lineStyle: { width: 2, color: '#34c98f' },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: 'rgba(52,201,143,0.28)' },
              { offset: 1, color: 'rgba(52,201,143,0)' }
            ])
          }
        }]
      })
    },
    formatBillingLabel(date) {
      if (!date) return date
      if (this.billingActivePeriod === 'annual') {
        const parts = date.split('-')
        return parseInt(parts[1]) + '月'
      }
      const parts = date.split('-')
      return parseInt(parts[2]) + '日'
    },
    async onReconcile() {
      this.reconciling = true
      try {
        const result = await this.$http.post('/admin/billing/reconcile')
        const pamcDiffCount = result.pamcDiffCount || 0
        const balanceDiffCount = result.balanceDiffCount || 0
        const totalDiff = pamcDiffCount + balanceDiffCount

        if (totalDiff === 0) {
          this.$alert(
            `<div class="reconcile-success">
              <i class="el-icon-success"></i>
              <p class="title">对账完成，无差异</p>
              <p class="subtitle">${result.startDate} 至 ${result.endDate}</p>
            </div>`,
            '对账结果',
            {
              dangerouslyUseHTMLString: true,
              confirmButtonText: '确定',
              customClass: 'dark-message-box'
            }
          )
        } else {
          this.$msgbox({
            title: '对账结果',
            message: `<div class="reconcile-warning">
              <div class="range">
                <span class="label">对账范围：</span>
                <span class="value">${result.startDate} 至 ${result.endDate}</span>
              </div>
              <div class="diff-box">
                <div class="diff-title">发现差异：</div>
                ${pamcDiffCount > 0 ? `<div class="diff-item"><span class="diff-label">pamc 差异：</span><span class="diff-count">${pamcDiffCount}</span> 条</div>` : ''}
                ${balanceDiffCount > 0 ? `<div class="diff-item"><span class="diff-label">余额差异：</span><span class="diff-count">${balanceDiffCount}</span> 条</div>` : ''}
              </div>
              <p class="suggest">建议点击"修复"按钮进行修复</p>
            </div>`,
            dangerouslyUseHTMLString: true,
            showCancelButton: true,
            confirmButtonText: '修复',
            cancelButtonText: '取消',
            customClass: 'dark-message-box warning-box'
          }).then(() => {
            this.onFix()
          }).catch(() => {
            // 用户取消，不做任何操作
          })
        }
      } catch (e) {
        this.$message.error('对账失败: ' + (e.message || e))
      } finally {
        this.reconciling = false
      }
    },
    async onFix() {
      this.fixing = true
      try {
        const result = await this.$http.post('/admin/billing/fix')
        const pamcFixed = result.pamcFixed || 0
        const balanceFixed = result.balanceFixed || 0
        this.$alert(
          `<div class="reconcile-success">
            <i class="el-icon-success"></i>
            <p class="title">修复完成</p>
            <div class="fix-stats">
              <div class="stat-item">
                <span class="stat-value">${pamcFixed}</span>
                <span class="stat-label">pamc 数据</span>
              </div>
              <div class="stat-item">
                <span class="stat-value">${balanceFixed}</span>
                <span class="stat-label">余额数据</span>
              </div>
            </div>
          </div>`,
          '修复结果',
          {
            dangerouslyUseHTMLString: true,
            confirmButtonText: '确定',
            customClass: 'dark-message-box'
          }
        )
      } catch (e) {
        this.$message.error('修复失败: ' + (e.message || e))
      } finally {
        this.fixing = false
      }
    }
  }
}
</script>

<style scoped>
.customer-page {
  padding: 18px 24px 76px;
}

.form-tip {
  font-size: 12px;
  color: var(--muted-2, #999);
  margin-top: 4px;
  line-height: 1.4;
}

.table-panel {
  padding: 16px;
  border-radius: var(--radius);
  box-shadow: var(--shadow);
}

.table-panel ::v-deep .el-table,
.table-panel ::v-deep .el-table__header-wrapper,
.table-panel ::v-deep .el-table__body-wrapper {
  border-radius: var(--radius);
}

.table-panel ::v-deep th {
  background: rgba(255, 255, 255, 0.045);
  color: #9eb1ca;
  font-weight: 850;
}

.table-panel ::v-deep td,
.table-panel ::v-deep th {
  border-color: var(--line-soft);
}

.pagination-bar {
  margin-top: 20px;
  text-align: right;
}

.toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 18px;
  flex-wrap: wrap;
}

.search-input {
  width: 200px;
}

.delete-btn {
  color: #e74c3c;
}

.enable-btn {
  color: #409eff;
}

.send-code-btn {
  color: #f59e0b;
}

.id-cell {
  display: flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
}

.id-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 70px;
  min-width: 40px;
  flex-shrink: 1;
  font-family: monospace;
}

.copy-btn {
  flex-shrink: 0;
  padding: 0;
  font-size: 14px;
  color: var(--muted-2, #999);
  line-height: 1;
}

.copy-btn:hover {
  color: #409eff;
}

.list-cards { display: grid; gap: 14px; }

.mobile-only { display: none; }

@media (max-width: 760px) {
  .desktop-only { display: none; }
  .mobile-only { display: block; }
  .customer-page {
    padding: 12px 12px 28px;
  }
  .table-panel {
    padding: 10px;
    overflow-x: visible;
  }
  .toolbar {
    flex-wrap: wrap;
  }
  .search-input {
    flex: 1;
    min-width: 80px;
  }
  .pagination-bar {
    text-align: center;
  }
}
.list-card {
  padding: 16px;
  border-radius: 10px;
  background: linear-gradient(135deg, rgba(27,39,58,.92), rgba(20,30,46,.88));
  border: 1px solid rgba(133,164,255,.12);
  box-shadow: 0 8px 28px rgba(0,0,0,.22);
  overflow: hidden;
  transition: transform .18s ease, box-shadow .18s ease;
}
.list-card:active {
  transform: translateY(-1px);
  box-shadow: 0 12px 36px rgba(0,0,0,.3);
}
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
  gap: 8px;
}
.card-title {
  font-size: 15px;
  font-weight: 700;
  color: var(--text);
  line-height: 1.3;
}
.card-sub {
  font-size: 12px;
  color: rgba(255,255,255,.4);
  margin-top: 2px;
}
.stats-row {
  display: flex;
  gap: 8px;
  margin-bottom: 10px;
  flex-wrap: wrap;
}
.stat-item {
  flex: 1;
  min-width: 0;
  background: rgba(255,255,255,.04);
  border-radius: 8px;
  padding: 8px 10px;
  text-align: center;
}
.stat-value {
  font-size: 14px;
  font-weight: 700;
  color: var(--text);
  line-height: 1.3;
}
.stat-label {
  font-size: 10px;
  color: rgba(255,255,255,.35);
  margin-top: 2px;
  letter-spacing: .02em;
}
.meta-row {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 12px;
  padding-top: 10px;
  border-top: 1px solid rgba(133,164,255,0.06);
  margin-top: 6px;
  font-size: 11px;
  color: rgba(255,255,255,.35);
}
.card-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding-top: 10px;
  border-top: 1px solid rgba(133,164,255,0.06);
  margin-top: 6px;
}
.card-actions .el-button--text {
  font-size: 12px;
  padding: 4px 10px;
  height: auto;
}

/* 账单弹窗样式 */
::v-deep .billing-fullscreen-dialog .el-dialog__body {
  padding: 20px 40px;
}

.billing-summary-cards {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 12px;
  margin: 16px 0;
}

.billing-summary-card {
  padding: 14px;
  border: 1px solid var(--line);
  border-radius: var(--radius);
  background: linear-gradient(180deg, rgba(27, 39, 58, 0.9), rgba(20, 30, 46, 0.88));
  box-shadow: var(--shadow);
}

.billing-card-label {
  font-size: 11px;
  color: var(--muted);
  margin-bottom: 6px;
  font-weight: 700;
}

.billing-card-value {
  font-size: 20px;
  font-weight: 800;
  color: var(--text);
  line-height: 1.1;
}

.profit-positive { color: #34c98f; }
.profit-negative { color: #ff6b7a; }

.billing-chart-panel {
  padding: 16px;
  margin-bottom: 14px;
  border: 1px solid var(--line);
  border-radius: var(--radius);
  background: linear-gradient(180deg, rgba(27, 39, 58, 0.9), rgba(20, 30, 46, 0.88));
}

.billing-chart-container {
  width: 100%;
  height: 220px;
}

.billing-panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.billing-panel-title {
  margin: 0;
  font-size: 15px;
  font-weight: 700;
  color: var(--text);
}

.billing-panel-sub {
  color: var(--muted);
  font-size: 12px;
}

.billing-table-panel {
  padding: 14px;
  border: 1px solid var(--line);
  border-radius: var(--radius);
  background: linear-gradient(180deg, rgba(27, 39, 58, 0.9), rgba(20, 30, 46, 0.88));
}

.billing-toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 14px;
}

.billing-toolbar-title {
  font-size: 15px;
  font-weight: 700;
  color: var(--text);
  flex: 1;
}

.billing-date-picker {
  width: 140px;
}

.billing-table-panel ::v-deep th {
  background: rgba(255, 255, 255, 0.045);
  color: #9eb1ca;
  font-weight: 700;
}

.billing-table-panel ::v-deep td,
.billing-table-panel ::v-deep th {
  border-color: var(--line-soft);
}

::v-deep .billing-dialog .el-dialog__body {
  padding: 16px 20px;
}

::v-deep .billing-fullscreen-dialog .el-tabs__header {
  border-bottom: 1px solid rgba(133, 164, 255, 0.15);
  margin-bottom: 20px;
}

::v-deep .billing-fullscreen-dialog .el-tabs__item {
  color: rgba(255, 255, 255, 0.35);
  font-size: 14px;
  font-weight: 800;
  height: 44px;
  line-height: 44px;
  letter-spacing: 0.02em;
}

::v-deep .billing-fullscreen-dialog .el-tabs__item:hover {
  color: var(--text);
}

::v-deep .billing-fullscreen-dialog .el-tabs__item.is-active {
  color: var(--text);
}

::v-deep .billing-fullscreen-dialog .el-tabs__active-bar {
  background: #55f0b3;
  height: 2.5px;
  border-radius: 2px;
}

::v-deep .billing-fullscreen-dialog .el-tabs__nav-wrap::after {
  background: transparent;
}

/* 对账成功样式 */
.reconcile-success {
  text-align: center;
  padding: 20px 0;
}

.reconcile-success i {
  font-size: 56px;
  color: #34c98f;
  display: block;
  margin-bottom: 16px;
}

.reconcile-success .title {
  font-size: 18px;
  font-weight: 700;
  color: var(--text);
  margin: 0 0 8px 0;
}

.reconcile-success .subtitle {
  font-size: 14px;
  color: var(--muted);
  margin: 0;
}

/* 修复统计样式 */
.fix-stats {
  display: flex;
  justify-content: center;
  gap: 32px;
  margin-top: 16px;
}

.fix-stats .stat-item {
  text-align: center;
}

.fix-stats .stat-value {
  display: block;
  font-size: 28px;
  font-weight: 800;
  color: #34c98f;
  line-height: 1;
  margin-bottom: 4px;
}

.fix-stats .stat-label {
  font-size: 12px;
  color: var(--muted);
}

/* 对账警告样式 */
.reconcile-warning .range {
  margin-bottom: 16px;
  font-size: 14px;
}

.reconcile-warning .range .label {
  color: var(--muted);
}

.reconcile-warning .range .value {
  color: var(--text);
  font-weight: 600;
}

.reconcile-warning .diff-box {
  background: rgba(255,107,122,.08);
  border: 1px solid rgba(255,107,122,.2);
  border-left: 4px solid #ff6b7a;
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 16px;
}

.reconcile-warning .diff-title {
  color: #ff6b7a;
  font-weight: 700;
  font-size: 14px;
  margin-bottom: 12px;
}

.reconcile-warning .diff-item {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  font-size: 14px;
  color: var(--text);
}

.reconcile-warning .diff-item:last-child {
  margin-bottom: 0;
}

.reconcile-warning .diff-label {
  color: var(--muted-2);
}

.reconcile-warning .diff-count {
  font-size: 18px;
  font-weight: 800;
  color: #ff6b7a;
}

.reconcile-warning .suggest {
  font-size: 13px;
  color: var(--muted-2);
  margin: 0;
}

/* 弹窗暗色主题 */
::v-deep .dark-message-box {
  background: linear-gradient(135deg, rgba(27,39,58,.98), rgba(20,30,46,.98)) !important;
  border: 1px solid rgba(133,164,255,.15) !important;
  box-shadow: 0 20px 60px rgba(0,0,0,.4) !important;
}

::v-deep .dark-message-box .el-message-box__header {
  border-bottom: 1px solid rgba(133,164,255,.1) !important;
  padding-bottom: 16px !important;
}

::v-deep .dark-message-box .el-message-box__title {
  color: var(--text) !important;
  font-size: 18px !important;
  font-weight: 700 !important;
}

::v-deep .dark-message-box .el-message-box__content {
  color: var(--text) !important;
  padding: 20px 0 !important;
}

::v-deep .dark-message-box .el-message-box__btns {
  border-top: 1px solid rgba(133,164,255,.1) !important;
  padding-top: 16px !important;
}

::v-deep .dark-message-box .el-button--default {
  background: rgba(255,255,255,.05) !important;
  border-color: rgba(133,164,255,.2) !important;
  color: var(--text) !important;
}

::v-deep .dark-message-box .el-button--default:hover {
  background: rgba(255,255,255,.1) !important;
  border-color: rgba(133,164,255,.4) !important;
}

::v-deep .dark-message-box .el-button--primary {
  background: linear-gradient(135deg, #34c98f, #2ab87e) !important;
  border: none !important;
  color: #fff !important;
}

::v-deep .dark-message-box .el-button--primary:hover {
  background: linear-gradient(135deg, #2ab87e, #20a76e) !important;
}

::v-deep .dark-message-box.warning-box .el-button--primary {
  background: linear-gradient(135deg, #ff6b7a, #ff5252) !important;
}

::v-deep .dark-message-box.warning-box .el-button--primary:hover {
  background: linear-gradient(135deg, #ff5252, #e03e3e) !important;
}
</style>
